import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { apiRequest } from '../../utils/api';
import { useAuth } from '../auth/AuthComponents';

export default function LawyerInquiryForm({ slug, onSuccess }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    sender_name: '',
    sender_email: '',
    sender_mobile: '',
    sender_whatsapp: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [conversationId, setConversationId] = useState(null);

  useEffect(() => {
    if (user) {
      setForm((prev) => ({
        ...prev,
        sender_name: user.full_name || '',
        sender_email: user.email || '',
      }));
    }
  }, [user]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const res = await apiRequest(`lawyers/slug/${slug}/inquire`, {
        method: 'POST',
        body: form,
      });
      setSuccess(res.message || 'Message sent successfully.');
      setConversationId(res.conversation_id || null);
      setForm((prev) => ({ ...prev, message: '' }));
      onSuccess?.();
    } catch (err) {
      setError(err.message || 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-8 pt-8 border-t border-[var(--color-border)]">
      <h3 className="text-lg font-semibold mb-1 flex items-center gap-2 text-[var(--color-text)]">
        <MessageCircle className="w-5 h-5 text-[var(--color-primary)]" />
        Send a message
      </h3>
      <p className="text-sm text-[var(--color-text-muted)] mb-5">
        Your details are sent to the lawyer by email and in-app notification so they can reach you directly.
      </p>

      {error ? (
        <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-[var(--color-danger)] text-sm">{error}</div>
      ) : null}
      {success ? (
        <div className="mb-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-[var(--color-success)] text-sm">
          {success}
          {conversationId && user ? (
            <Link to={`/messages/${conversationId}`} className="block mt-2 text-[var(--color-primary)] font-semibold hover:text-[var(--color-primary-hover)]">
              View message →
            </Link>
          ) : null}
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5">
              Your Name <span className="text-[var(--color-danger)]">*</span>
            </label>
            <input
              required
              value={form.sender_name}
              onChange={handleChange('sender_name')}
              placeholder="Enter your name"
              className="themed-input w-full px-4 py-2.5 rounded-xl focus:outline-none focus:border-[var(--color-primary)]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5">
              Your Email <span className="text-[var(--color-danger)]">*</span>
            </label>
            <input
              required
              type="email"
              value={form.sender_email}
              onChange={handleChange('sender_email')}
              placeholder="Enter your e-mail"
              className="themed-input w-full px-4 py-2.5 rounded-xl focus:outline-none focus:border-[var(--color-primary)]"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5">Your Mobile</label>
            <input
              value={form.sender_mobile}
              onChange={handleChange('sender_mobile')}
              placeholder="Enter your mobile number"
              className="themed-input w-full px-4 py-2.5 rounded-xl focus:outline-none focus:border-[var(--color-primary)]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5">Your WhatsApp No</label>
            <input
              value={form.sender_whatsapp}
              onChange={handleChange('sender_whatsapp')}
              placeholder="Enter your WhatsApp number"
              className="themed-input w-full px-4 py-2.5 rounded-xl focus:outline-none focus:border-[var(--color-primary)]"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5">
            Your Message <span className="text-[var(--color-danger)]">*</span>
          </label>
          <textarea
            required
            rows={5}
            value={form.message}
            onChange={handleChange('message')}
            placeholder="Type your message here"
            className="themed-input w-full px-4 py-2.5 rounded-xl focus:outline-none focus:border-[var(--color-primary)] resize-y min-h-[120px]"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="brand-gradient-btn w-full sm:w-auto px-6 py-3 rounded-xl disabled:opacity-50 font-semibold"
        >
          {submitting ? 'Sending…' : 'Send Message'}
        </button>
      </form>
    </div>
  );
}
